import React, { useEffect, useRef, useState } from "react";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./batchScanner.module.scss";

/**
 * Batch scanning: preload the scannable package set once, then resolve every
 * subsequent scan 100% locally (camera multi-barcode-per-frame, or a
 * dedicated hardware-scanner input) - no network round-trip per scan.
 * Calls onScannedChange(scannedPackages) whenever the running list changes.
 *
 * enforceSingleClient: when true, whichever client the first scanned package
 * belongs to "locks" the session - any later scan for a different client is
 * rejected immediately (not added) instead of only being caught at submit
 * time. Used by in-store pickup (one walk-in customer per session); left off
 * for airport handover, which intentionally groups multiple clients.
 */
const BatchScanner = ({ scannableEndpoint, onScannedChange, lookupEndpoint, enforceSingleClient }) => {
    const [catalog, setCatalog] = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [inputMode, setInputMode] = useState(null); // "camera" | "gun"
    const [scanned, setScanned] = useState([]);
    const gunInputRef = useRef(null);
    const gunBufferRef = useRef("");
    const lastScanRef = useRef({ code: "", time: 0 });
    // Ref (not state) so addScan - called from event listeners that can hold
    // a stale closure over `scanned` - always sees the latest locked client.
    // Kept in sync inside the setScanned updaters below, never read from the
    // render-scope `scanned` directly.
    const lockedClientIdRef = useRef(null);

    const clientIdOf = (pkg) => pkg?.name?._id || pkg?.name || null;

    useEffect(() => {
        const loadCatalog = async () => {
            setLoadingCatalog(true);
            const res = await handleGetRequest(scannableEndpoint);
            setCatalog(res?.data || []);
            setLoadingCatalog(false);
        };
        loadCatalog();
    }, [scannableEndpoint]);

    useEffect(() => {
        if (onScannedChange) onScannedChange(scanned);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanned]);

    const findInCatalog = (code) => {
        const normalized = code.trim();
        return catalog.find((pkg) => pkg.Waybill === normalized || pkg.trackingCode === normalized || pkg.barcode === normalized);
    };

    // On a cache miss, ask the backend why - the code usually does belong to
    // a real package that's just already Delivered/Handed Over/Picked Up
    // (and so deliberately left out of the scannable catalog), which reads
    // very differently from a code that isn't a package at all. Only fires
    // on a miss, so the normal scan loop stays 100% local.
    const explainUnrecognized = async (code) => {
        if (!lookupEndpoint) {
            toast.error(`Not recognized: ${code}`);
            return;
        }
        const res = await handleGetRequest(`${lookupEndpoint}/${encodeURIComponent(code)}`);
        if (res?.success && res?.data) {
            toast.error(`${res.data.Waybill || code} is already "${res.data.packageStatus || "done"}" - not eligible for pickup`);
        } else {
            toast.error(`Not recognized: ${code}`);
        }
    };

    const addScan = (code) => {
        const now = Date.now();
        if (lastScanRef.current.code === code && now - lastScanRef.current.time < 1500) return;
        lastScanRef.current = { code, time: now };

        const match = findInCatalog(code);
        if (!match) {
            explainUnrecognized(code);
            return;
        }

        if (enforceSingleClient && lockedClientIdRef.current && clientIdOf(match) !== lockedClientIdRef.current) {
            toast.error(`${match.Waybill} belongs to a different client - not added. Remove the current scans first or start a separate pickup for them.`);
            return;
        }

        setScanned((prev) => {
            if (prev.some((p) => p._id === match._id)) {
                toast.info(`Already scanned: ${match.Waybill}`);
                return prev;
            }
            toast.success(`Scanned: ${match.Waybill}`);
            const next = [...prev, match];
            if (enforceSingleClient) lockedClientIdRef.current = clientIdOf(next[0]);
            return next;
        });
    };

    const removeScan = (packageId) => {
        setScanned((prev) => {
            const next = prev.filter((p) => p._id !== packageId);
            if (enforceSingleClient) lockedClientIdRef.current = clientIdOf(next[0]);
            return next;
        });
    };

    // Hardware scanner: keeps a dedicated input always focused while in "gun"
    // mode, buffering keystrokes and treating Enter as end-of-scan. Scoped to
    // this input only (not a window-level listener) so typing elsewhere on
    // the page is never mistaken for a scan.
    useEffect(() => {
        if (inputMode !== "gun") return;
        const input = gunInputRef.current;
        if (!input) return;

        const focusInput = () => input.focus();
        focusInput();

        const handleKeyDown = (e) => {
            if (e.key === "Enter") {
                const code = gunBufferRef.current.trim();
                gunBufferRef.current = "";
                if (code) addScan(code);
            } else if (e.key.length === 1) {
                gunBufferRef.current += e.key;
            }
        };

        input.addEventListener("keydown", handleKeyDown);
        document.addEventListener("click", focusInput);
        return () => {
            input.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("click", focusInput);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputMode, catalog]);

    const handleCameraScan = (err, result) => {
        if (result?.text) addScan(result.text);
    };

    if (loadingCatalog) {
        return (
            <div className={styles.loadingCatalog}>
                <i className="pi pi-spin pi-spinner" />
                Loading scannable packages...
            </div>
        );
    }

    if (!inputMode) {
        return (
            <div className={styles.scanner}>
                <div className={styles.modeChoice}>
                    <button type="button" onClick={() => setInputMode("gun")}>
                        <i className="pi pi-barcode" />
                        Barcode Scanner
                    </button>
                    <button type="button" onClick={() => setInputMode("camera")}>
                        <i className="pi pi-camera" />
                        Camera
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.scanner}>
            {inputMode === "gun" && (
                <>
                    <div className={styles.gunHint}>
                        <i className="pi pi-barcode" />
                        Ready to scan - point the scanner at each barcode.
                    </div>
                    <input ref={gunInputRef} className={styles.scannerInput} autoFocus readOnly value="" onChange={() => {}} />
                </>
            )}

            {inputMode === "camera" && (
                <div className={styles.cameraContainer}>
                    <BarcodeScannerComponent width="100%" height={280} onUpdate={handleCameraScan} />
                </div>
            )}

            <div className={styles.counter}>
                <h4>Scanned ({scanned.length})</h4>
                <Button label="Switch Input" icon="pi pi-refresh" text onClick={() => setInputMode(null)} />
            </div>

            <div className={styles.scannedList}>
                {scanned.length === 0 ? (
                    <div className={styles.emptyState}>No packages scanned yet</div>
                ) : (
                    scanned.map((pkg) => (
                        <div key={pkg._id} className={styles.scannedItem}>
                            <div className={styles.scannedIcon}>
                                <i className="pi pi-check" />
                            </div>
                            <div className={styles.info}>
                                <strong>{pkg.Waybill}</strong>
                                <span>
                                    {pkg.name?.firstName} {pkg.name?.lastName}
                                </span>
                            </div>
                            <Button icon="pi pi-times" className="p-button-text p-button-danger" onClick={() => removeScan(pkg._id)} />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BatchScanner;
